import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Package, Users, TrendingUp, Trophy, Activity, Plus, Bot, Rocket } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import EmptyStateCards from "../components/EmptyStateCards";

const EUR = (v) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

const STAGE_LABELS = {
  neu: "Neu",
  angebot: "Angebot",
  verhandlung: "Verhandlung",
  gewonnen: "Gewonnen",
  verloren: "Verloren",
};
const STAGE_COLORS = {
  neu: "bg-slate-500",
  angebot: "bg-amber-500",
  verhandlung: "bg-violet-500",
  gewonnen: "bg-emerald-500",
  verloren: "bg-red-500",
};
const LEAD_STATUS_LABELS = {
  neu: "Neu",
  kontaktiert: "Kontaktiert",
  qualifiziert: "Qualifiziert",
  verloren: "Verloren",
};

export default function Dashboard() {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/metrics").then((r) => { setM(r.data); setLoading(false); });
  }, []);

  if (loading || !m) {
    return <div className="p-8 text-slate-400" data-testid="dashboard-loading">Lade Übersicht…</div>;
  }

  const projectEmpty = (m.total_products || 0) + (m.total_leads || 0) + (m.total_deals || 0) === 0;
  if (projectEmpty) {
    return (
      <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="dashboard-page">
        <header className="mb-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Willkommen</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 text-white">Starte hier</h1>
        </header>
        <EmptyStateCards
          testid="dashboard-empty"
          title="Dein Projekt ist noch leer"
          subtitle="Erzähl dem Agenten in einem Satz, was du machen willst — er legt los."
          cards={[
            { icon: Bot, title: "KI-Agent fragen", body: "Der schnellste Start — ein Satz reicht.", to: "/ki-agent", testid: "dashboard-card-agent" },
            { icon: Plus, title: "Produkt anlegen", body: "Lieber selbst klicken? Starte mit einem Produkt.", to: "/produkte", testid: "dashboard-card-produkt" },
            { icon: Rocket, title: "Landingpage bauen", body: "Sammle Leads automatisch über eine eigene Seite.", to: "/seiten", testid: "dashboard-card-lp" },
          ]}
          primaryTestid="dashboard-card-agent"
        />
      </div>
    );
  }

  const stages = ["neu", "angebot", "verhandlung", "gewonnen", "verloren"];
  const maxStageValue = Math.max(1, ...stages.map((s) => m.deals_by_stage[s]?.value || 0));

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="dashboard-page">
      <header className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Übersicht</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 text-white">Dashboard</h1>
        <p className="text-slate-400 text-sm mt-2">Live-Metriken deines Commerce- und Sales-Betriebs.</p>
      </header>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        <KpiCard testid="kpi-total-products" icon={Package} label="Gesamtprodukte" value={m.total_products} sub={`${m.active_products} aktiv`} tint="emerald" />
        <KpiCard testid="kpi-active-leads" icon={Users} label="Aktive Leads" value={m.total_leads} sub={`${m.leads_by_status.qualifiziert || 0} qualifiziert`} tint="indigo" />
        <KpiCard testid="kpi-pipeline-value" icon={TrendingUp} label="Pipeline-Wert (offen)" value={EUR(m.pipeline_value_open)} sub={`${m.total_deals} Deals gesamt`} tint="amber" />
        <KpiCard testid="kpi-win-rate" icon={Trophy} label="Abschlussquote" value={`${m.win_rate}%`} sub={EUR(m.won_value) + " gewonnen"} tint="violet" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pipeline by stage */}
        <Card className="lg:col-span-2 bg-[#111827] border-white/5 text-slate-100" data-testid="dashboard-pipeline-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold tracking-tight">Pipeline-Wert nach Stufe</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {stages.map((s) => {
              const row = m.deals_by_stage[s] || { count: 0, value: 0 };
              const pct = (row.value / maxStageValue) * 100;
              return (
                <div key={s} data-testid={`dashboard-stage-${s}`}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block h-2 w-2 rounded-full ${STAGE_COLORS[s]}`} />
                      <span className="font-medium">{STAGE_LABELS[s]}</span>
                      <Badge variant="outline" className="border-white/10 text-slate-400 text-[10px]">{row.count}</Badge>
                    </div>
                    <span className="text-slate-400 tabular-nums">{EUR(row.value)}</span>
                  </div>
                  <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full ${STAGE_COLORS[s]} transition-all duration-500`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Leads by status */}
        <Card className="bg-[#111827] border-white/5 text-slate-100" data-testid="dashboard-leads-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold tracking-tight">Leads nach Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(m.leads_by_status).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm" data-testid={`dashboard-lead-status-${k}`}>
                <span className="text-slate-300">{LEAD_STATUS_LABELS[k] || k}</span>
                <span className="text-white font-semibold tabular-nums">{v}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recent activity */}
        <Card className="lg:col-span-3 bg-[#111827] border-white/5 text-slate-100" data-testid="dashboard-activity-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold tracking-tight flex items-center gap-2">
              <Activity className="h-4 w-4 text-emerald-400" /> Letzte Aktivitäten
            </CardTitle>
          </CardHeader>
          <CardContent>
            {m.recent_activity.length === 0 ? (
              <div className="text-sm text-slate-500">Noch keine Aktivitäten.</div>
            ) : (
              <ul className="divide-y divide-white/5">
                {m.recent_activity.map((a, i) => (
                  <li key={i} className="py-2.5 flex items-center justify-between text-sm" data-testid={`activity-item-${i}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge variant="outline" className="border-white/10 text-slate-400 uppercase tracking-wider text-[10px]">
                        {a.type}
                      </Badge>
                      <span className="truncate">{a.label}</span>
                    </div>
                    <span className="text-slate-500 text-xs whitespace-nowrap pl-4">
                      {a.timestamp ? formatDistanceToNow(parseISO(a.timestamp), { addSuffix: true, locale: de }) : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tint, testid }) {
  const tintMap = {
    emerald: "text-emerald-400 bg-emerald-500/10",
    indigo: "text-indigo-400 bg-indigo-500/10",
    amber: "text-amber-400 bg-amber-500/10",
    violet: "text-violet-400 bg-violet-500/10",
  };
  return (
    <Card className="bg-[#111827] border-white/5 text-slate-100" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
          <div className={`h-9 w-9 rounded-lg grid place-items-center ${tintMap[tint]}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <div className="text-3xl font-extrabold tracking-tight mt-3 text-white">{value}</div>
        <div className="text-xs text-slate-500 mt-1">{sub}</div>
      </CardContent>
    </Card>
  );
}
